import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  ArrowRight,
  CalendarDays,
  CheckCheck,
  ClipboardCheck,
  Clock,
  FileImage,
  History,
  Lock,
  MapPin,
  Pencil,
  Phone,
  Route,
  Trash2,
  Truck,
  UserRound,
} from 'lucide-react';
import { Modal } from '../../../components/ui/Modal';
import { areaClass } from '../../../components/ui/form-classes';
import { byId } from '../../../core/assignment-rules';
import { DISPATCH_CARD_TITLE, buildTaskDispatchCard } from '../../../core/dispatch-card';
import { navigationLinks, telHref } from '../../../core/field-links';
import { formatPhone } from '../../../core/phone';
import { contactOf, formatTaskDate, formatTaskWindow, taskTypeName } from '../../../core/task-filters';
import { assignedSoldierIdsOf } from '../../../core/task-views';
import { closeTaskAsManager, updateTask } from '../../../db/task-store';
import { describeAuditLog } from '../../../db/audit-store';
import type { AuditLogEntry } from '../../../types/audit';
import type { TaskTeam } from '../../../types/assignment';
import type { Contact } from '../../../types/contact';
import {
  SOLDIER_TASK_STATE_LABELS,
  SOLDIER_TASK_STATE_TONES,
  allSoldiersFinished,
  soldierTaskState,
} from '../../../types/execution';
import type { SoldierTaskProgress } from '../../../types/execution';
import type { Soldier, Vehicle } from '../../../types/fleet';
import {
  TASK_PRIORITY_LABELS,
  TASK_PRIORITY_TONES,
  TASK_STATUSES,
  TASK_STATUS_LABELS,
  normalizeTaskStatus,
  taskStatusLabel,
  taskStatusTone,
} from '../../../types/task';
import type { Task, TaskStatusId, TaskType } from '../../../types/task';
import type { TimelineEvent } from '../../../types/timeline';
import type { User } from '../../../types/user';
import { DispatchOrderCard } from '../DispatchOrderCard';
import { ShareDispatchButton } from '../ShareDispatchButton';
import { TaskTeamCard } from '../assignment';
import { TaskTimeline } from '../timeline';

interface ManagerTaskViewProps {
  user: User;
  task: Task;
  taskTypes: readonly TaskType[];
  contacts: readonly Contact[];
  vehicles: readonly Vehicle[];
  soldiers: readonly Soldier[];
  teams: readonly TaskTeam[];
  /** One row per soldier that reported from the field. */
  progress: readonly SoldierTaskProgress[];
  /** The task's ledger rows, newest first. */
  timeline: readonly AuditLogEntry[];
  /** The operational feed — every step reported on the task. */
  timelineEvents: readonly TimelineEvent[];
  canEdit: boolean;
  canAssign: boolean;
  canClose: boolean;
  canViewInternalNotes: boolean;
  onBack: () => void;
  /** Opens the shared task form. */
  onEdit?: (task: Task) => void;
  /** Opens the shared vehicle-teams dialog. */
  onManageTeams?: (task: Task) => void;
  /** Raises the board's delete confirmation — omitted without `DELETE_TASK`. */
  onDelete?: (task: Task) => void;
  onReload: () => void | Promise<void>;
}

function Section({ title, icon, children, action }: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-line bg-panel p-4 space-y-3 text-right">
      <header className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold text-ink/70 flex items-center gap-2">
          <span className="text-brand-500" aria-hidden>
            {icon}
          </span>
          {title}
        </h3>
        {action}
      </header>
      {children}
    </section>
  );
}

/**
 * The manager's whole view of one task: the operational header with the status
 * controls, the manager-only notes, every vehicle team with what its crew has
 * reported, the task's audit timeline and the closure button.
 *
 * The separation the screen is built around: a soldier reporting "סיום ביצוע"
 * is a line in the crew table; closing the task is a deliberate act taken here,
 * and only here.
 */
export function ManagerTaskView({
  user,
  task,
  taskTypes,
  contacts,
  vehicles,
  soldiers,
  teams,
  progress,
  timeline,
  timelineEvents,
  canEdit,
  canAssign,
  canClose,
  canViewInternalNotes,
  onBack,
  onEdit,
  onManageTeams,
  onDelete,
  onReload,
}: ManagerTaskViewProps) {
  const [closing, setClosing] = useState(false);
  const [closingNote, setClosingNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now] = useState(() => Date.now());

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await onReload();
      setClosing(false);
      setClosingNote('');
    } catch (err) {
      setError(`הפעולה נכשלה — ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const contact = contactOf(task, contacts);
  const window = formatTaskWindow(task);
  const typeName = taskTypeName(task.taskTypeId, taskTypes);
  const navLinks = navigationLinks(task);
  // The whole convoy, as the portrait order a manager sends to the field.
  const dispatchCard = useMemo(
    () =>
      buildTaskDispatchCard(task, { teams, vehicles, soldiers, contacts, taskTypes, now }),
    [task, teams, vehicles, soldiers, contacts, taskTypes, now],
  );
  const crewIds = assignedSoldierIdsOf(teams);
  const everyoneFinished = allSoldiersFinished(crewIds, progress);
  const isClosed = Boolean(task.closedAt);

  return (
    <div className="mx-auto w-full max-w-3xl space-y-3">
      <button
        type="button"
        onClick={onBack}
        className="h-10 px-3 rounded-xl border border-line text-sm text-ink/60 hover:text-ink flex items-center gap-1.5"
      >
        <ArrowRight size={16} aria-hidden />
        חזרה ללוח המשימות
      </button>

      {/* ── Header: what, when, how pressing, and where it stands ── */}
      <header className="rounded-2xl border border-line bg-panel p-4 space-y-3 text-right">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-bold leading-tight">{task.title}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink/60">
              {typeName ? (
                <span className="px-2 py-0.5 rounded-lg border border-line bg-canvas">{typeName}</span>
              ) : null}
              <span className="flex items-center gap-1.5">
                <CalendarDays size={14} className="text-ink/40" aria-hidden />
                {formatTaskDate(task.date, now)}
              </span>
              {window ? (
                <span className="flex items-center gap-1.5">
                  <Clock size={14} className="text-ink/40" aria-hidden />
                  <span data-dir="ltr" className="tabular-nums">
                    {window}
                  </span>
                </span>
              ) : null}
            </div>
          </div>
          <span
            className={[
              'shrink-0 text-sm px-2.5 py-1 rounded-lg border font-bold',
              TASK_PRIORITY_TONES[task.priority],
            ].join(' ')}
          >
            {TASK_PRIORITY_LABELS[task.priority]}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span
            className={['text-sm px-2.5 py-1 rounded-lg border', taskStatusTone(task.statusId)].join(' ')}
          >
            {taskStatusLabel(task.statusId)}
          </span>
          {isClosed ? (
            <span className="text-xs px-2 py-1 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-700 flex items-center gap-1.5">
              <Lock size={12} aria-hidden />
              נסגרה על ידי מנהל
            </span>
          ) : null}
        </div>

        {/* Operational status control — the manager's own lever. */}
        {canEdit ? (
          <label className="flex items-center gap-2 text-sm">
            <span className="text-ink/60">סטטוס תפעולי:</span>
            <select
              value={normalizeTaskStatus(task.statusId)}
              disabled={busy}
              onChange={(event) =>
                void run(() =>
                  updateTask(user, task.id, { statusId: event.target.value as TaskStatusId }),
                )
              }
              className="h-10 rounded-xl border border-line bg-canvas px-3 text-sm"
            >
              {TASK_STATUSES.map((statusId) => (
                <option key={statusId} value={statusId}>
                  {TASK_STATUS_LABELS[statusId]}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {canEdit && onEdit ? (
            <button
              type="button"
              onClick={() => onEdit(task)}
              className="h-10 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink flex items-center gap-1.5"
            >
              <Pencil size={15} aria-hidden />
              עריכת פרטי המשימה
            </button>
          ) : null}
          {canAssign && onManageTeams ? (
            <button
              type="button"
              onClick={() => onManageTeams(task)}
              className="h-10 px-4 rounded-xl border border-brand-300 bg-brand-50 text-brand-700 text-sm font-medium flex items-center gap-1.5"
            >
              <Truck size={15} aria-hidden />
              ניהול צוותים ורכבים
            </button>
          ) : null}
          {onDelete ? (
            <button
              type="button"
              onClick={() => onDelete(task)}
              className="h-10 px-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-sm font-medium hover:bg-rose-100 flex items-center gap-1.5"
            >
              <Trash2 size={15} aria-hidden />
              מחיקת המשימה
            </button>
          ) : null}
        </div>

        {task.description ? <p className="text-sm text-ink/70">{task.description}</p> : null}
        {task.operationalNotes ? (
          <p className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            {task.operationalNotes}
          </p>
        ) : null}
      </header>

      {/* ── The order as it is sent to the field: portrait, and shareable ── */}
      {dispatchCard ? (
        <Section title={DISPATCH_CARD_TITLE} icon={<FileImage size={16} />}>
          <DispatchOrderCard
            card={dispatchCard}
            actions={<ShareDispatchButton card={dispatchCard} size="lg" />}
          />
        </Section>
      ) : null}

      {/* ── Manager-only notes — never rendered without VIEW_ALL_TASKS ── */}
      {canViewInternalNotes ? (
        <Section title="הערות פנימיות למנהלים" icon={<Lock size={16} />}>
          {task.internalNotes ? (
            <p className="rounded-xl border border-line bg-canvas p-3 text-sm text-ink/70">
              {task.internalNotes}
            </p>
          ) : (
            <p className="text-sm text-ink/40">לא נכתבו הערות פנימיות למשימה זו.</p>
          )}
          <p className="text-xs text-ink/40">
            הערות אלו אינן מוצגות לחיילים בשטח בשום מסך.
          </p>
        </Section>
      ) : null}

      {/* ── Contact & destination ── */}
      <Section title="איש קשר ויעד" icon={<UserRound size={16} />}>
        {contact ? (
          <p className="text-sm text-ink/70 flex flex-wrap items-center gap-2">
            <span className="font-medium">{contact.name}</span>
            {contact.role ? <span className="text-ink/50">{contact.role}</span> : null}
            {contact.phone ? (
              <a
                href={telHref(contact.phone) ?? '#'}
                data-dir="ltr"
                className="tabular-nums text-brand-700 flex items-center gap-1.5"
              >
                <Phone size={14} aria-hidden />
                {formatPhone(contact.phone)}
              </a>
            ) : null}
          </p>
        ) : (
          <p className="text-sm text-ink/40">לא הוגדר איש קשר.</p>
        )}
        {task.destinationAddress ? (
          <p className="text-sm text-ink/70 flex items-start gap-2">
            <MapPin size={15} className="text-ink/40 mt-0.5 shrink-0" aria-hidden />
            <span>{task.destinationAddress}</span>
          </p>
        ) : null}
        {navLinks.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {navLinks.map((link) => (
              <a
                key={link.id}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="h-9 px-3 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
              >
                {link.label}
              </a>
            ))}
          </div>
        ) : null}
      </Section>

      {/* ── The convoy, with what each crew reported ── */}
      <Section
        title="רכבים וצוותים"
        icon={<Truck size={16} />}
        action={
          canAssign && onManageTeams ? (
            <button
              type="button"
              onClick={() => onManageTeams(task)}
              className="h-9 px-3 rounded-xl border border-brand-300 bg-brand-50 text-brand-700 text-sm font-medium"
            >
              שיבוץ
            </button>
          ) : null
        }
      >
        {teams.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-line p-4 text-center text-sm text-ink/50">
            טרם שובצו רכבים למשימה.
          </p>
        ) : (
          <div className="space-y-3">
            {teams.map((team) => (
              <div key={team.id} className="space-y-2">
                <TaskTeamCard team={team} vehicles={vehicles} soldiers={soldiers} />
                <ul className="flex flex-wrap gap-1.5">
                  {crewIds
                    .filter((soldierId) =>
                      [team.driverId, team.escortId, ...team.passengers.map((p) => p.soldierId)]
                        .includes(soldierId),
                    )
                    .map((soldierId) => {
                      const row = progress.find((entry) => entry.soldierId === soldierId) ?? null;
                      const state = soldierTaskState(row);
                      return (
                        <li
                          key={soldierId}
                          className={[
                            'text-xs px-2 py-1 rounded-lg border',
                            SOLDIER_TASK_STATE_TONES[state],
                          ].join(' ')}
                        >
                          {byId(soldiers, soldierId)?.fullName ?? soldierId} ·{' '}
                          {SOLDIER_TASK_STATE_LABELS[state]}
                        </li>
                      );
                    })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ── The operational feed: where the task stands, step by step ── */}
      <Section title="ציר זמן תפעולי" icon={<Route size={16} />}>
        <TaskTimeline
          events={timelineEvents}
          order="desc"
          now={now}
          emptyLabel="טרם דווחו שלבים תפעוליים במשימה זו."
        />
      </Section>

      {/* ── The ledger: who took responsibility, and for what ── */}
      <Section title="יומן המשימה" icon={<History size={16} />}>
        {timeline.length === 0 ? (
          <p className="text-sm text-ink/40">טרם נרשמו אירועים למשימה זו.</p>
        ) : (
          <ul className="space-y-1.5">
            {timeline.map((entry) => (
              <li key={entry.id} className="rounded-xl border border-line bg-canvas p-2.5 text-sm text-ink/70">
                {describeAuditLog(entry)}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* ── Closure — the manager's act, separate from any soldier's ── */}
      <Section title="סגירת המשימה" icon={<ClipboardCheck size={16} />}>
        <p className="text-sm text-ink/60 flex items-center gap-2">
          <CheckCheck size={15} className="text-ink/40" aria-hidden />
          {crewIds.length === 0
            ? 'אין עדיין צוות משובץ למשימה.'
            : everyoneFinished
              ? 'כל אנשי הצוות דיווחו על סיום ביצוע.'
              : 'טרם כל אנשי הצוות דיווחו על סיום ביצוע.'}
        </p>
        {isClosed ? (
          <p className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800">
            המשימה נסגרה{task.closingNote ? ` — ${task.closingNote}` : ''}.
          </p>
        ) : canClose ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => setClosing(true)}
            className="h-12 px-5 rounded-2xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
          >
            <ClipboardCheck size={17} aria-hidden />
            סגירת משימה סופית
          </button>
        ) : (
          <p className="text-sm text-ink/40">נדרשת הרשאת עריכת משימה (EDIT_TASK) לסגירת המשימה.</p>
        )}
        {error ? (
          <p className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</p>
        ) : null}
      </Section>

      {closing ? (
        <Modal
          title="סגירת משימה סופית"
          subtitle={task.title}
          onClose={() => setClosing(false)}
          footer={
            <>
              <button
                type="button"
                onClick={() => setClosing(false)}
                className="h-11 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
              >
                ביטול
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void run(() => closeTaskAsManager(user, task.id, closingNote))}
                className="h-11 px-5 rounded-xl bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50"
              >
                סגירת המשימה
              </button>
            </>
          }
        >
          <p className="text-sm text-ink/60">
            סגירה מעבירה את המשימה לסטטוס "הושלמה" ומתעדת מי סגר אותה ומתי. דיווח החייל על סיום
            ביצוע אינו סוגר את המשימה.
          </p>
          <label className="mt-3 block space-y-1.5">
            <span className="text-sm text-ink/60">הערת סגירה (רשות)</span>
            <textarea
              value={closingNote}
              onChange={(event) => setClosingNote(event.target.value)}
              rows={3}
              className={areaClass}
              placeholder="לדוגמה: הציוד נמסר במלואו, אין חריגים."
            />
          </label>
        </Modal>
      ) : null}
    </div>
  );
}
